'use strict';

import debounce from 'lodash/debounce';
import * as progress from './progress';
import {initModules} from '../helpers/utilities';
import {appendParamToURL} from '../helpers/urlUtilities';
import {setOverlay, unsetOverlay, allowScrollableAreas, addEventListenerList} from '../helpers/utilities';
import {disableBodyScroll, enableBodyScroll} from 'body-scroll-lock';
import {callbackOnElements} from '@borngroup/born-utilities';
import modules from '../helpers/modules';

const
    SUGGESTIONS_URL_EL = document.querySelector('[data-cms-suggestions-url]'),
    ENDPOINT = SUGGESTIONS_URL_EL ? SUGGESTIONS_URL_EL.getAttribute('data-cms-suggestions-url') : '',
    SEARCH_CATEGORY = 'faq-search',
    MIN_CHARS = 3,
    isSRP = document.querySelector('[data-faq-srp]') != null;

/**
 * Retrieves the closest search container, or the currently available one.
 */
function getSearchContainer(scope) {
    let selector = '[data-cms-search]';

    return scope ? scope.closest(selector) : document.querySelector(selector);
}

/**
 * Retrieves Suggestions element relative to scope
 *
 * @param {Object} scope - Search input field DOM element
 * @return {JQuery} - [data-search-component="suggestions-container"] element
 */
function getSuggestionsContainer(scope, emptyContainer) {
    let searchContainer = getSearchContainer(scope),
        suggestionsContainer = searchContainer ? searchContainer.querySelector('[data-search-component="suggestions-container"]') : false;

    if (emptyContainer && suggestionsContainer) {
        suggestionsContainer.textContent = '';
        setupSelector(searchContainer, 'remove', 'set--has-results');
    }

    return suggestionsContainer;
}

function _handleSearchTracking(options) {
    modules.tracking.init(null, {
        trackEvent: 'search',
        trackObject: {
            searchTerm: options.searchTerm,
            searchCategory: options.searchCategory || 'faq-search'
        }
    });
}

/**
 * Process Ajax response for SearchServices-GetSuggestions
 *
 * @param {Object|string} response - Empty object literal if null response or string with rendered
 *                                   suggestions template contents
 */
function processContentResponse(response, formUrl, searchTerm, isDefault) {
    let suggestionsContainer = getSuggestionsContainer(this),
        searchContainer = getSearchContainer(this),
        suggestionsResultContainer = suggestionsContainer.querySelector('[data-component="faq-search-suggestions-result"]'),
        suggestionsDefaultContainer = suggestionsContainer.querySelector('[data-component="faq-search-suggestions-default"]'),
        searchNoResultsInfo = document.querySelector('[data-component="faq-search-no-results-info"]'),
        mostPopularSearches = document.querySelector('[data-component="cms-help-popular-searches"]');

    hideSrpNoResult();
    initModules(suggestionsContainer);

    if (typeof response !== 'object') {
        if (isDefault === 'isDefault') {
            suggestionsDefaultContainer.innerHTML = response;
        }
    } else {
        suggestionsResultContainer.innerHTML = response.renderedTemplate;
        setupSelector(suggestionsResultContainer, 'remove', 'hidden');

        if (response.suggestions.contentCount > 0) {
            setupSelector(suggestionsDefaultContainer, 'add', 'hidden');
            setupSelector(searchNoResultsInfo, 'add', 'hidden');
        } else {
            setupSelector(suggestionsDefaultContainer, 'remove', 'hidden');
            setupSelector(searchNoResultsInfo, 'remove', 'hidden');
            setupSelector(mostPopularSearches, 'add', 'hidden');
        }

        if (isSRP) {
            document.querySelector('body').classList.add('set--srp-has-search-results');
        }
    }

    setupSelector(searchContainer, 'add', 'set--has-results');
    setupSelector(suggestionsContainer, 'remove', 'set--invisible');

    progress.hideLoader();
}

/**
 * Handles selector class
 * @param {Node} element
 * @param {String} action
 * @param {String} className
 */

function setupSelector(element, action, className) {
    if (element) {
        element.classList[action](className);
    }
}

function showSearchResultOverlay() {
    let suggestionsContainer = getSuggestionsContainer(this);

    setupSelector(suggestionsContainer, 'remove', 'set--invisible');

    if (!isSRP) {
        document.querySelector('body').classList.add('set--has-search-results');
    }
}

/**
 * Retrieve suggestions
 *
 * @param {Object} scope - Search field DOM element
 */
function getSuggestions() {
    let searchTerm = this.form['q'].value,
        suggestionsDefaultContainer = document.querySelector('[data-component="faq-search-suggestions-default"]'),
        suggestionsResultContainer = document.querySelector('[data-component="faq-search-suggestions-result"]'),
        searchNoResultsInfo = document.querySelector('[data-component="faq-search-no-results-info"]'),
        mostPopularSearches = document.querySelector('[data-component="cms-help-popular-searches"]'),
        searchContainer = document.querySelector('[data-cms-search]');

    if (searchTerm.length >= MIN_CHARS) {
        let formUrl = appendParamToURL(ENDPOINT, 'q', searchTerm);

        progress.showLoader(getSuggestionsContainer(this));

        if (this.form['cgid'] && this.form['cgid'].checked) {
            formUrl = appendParamToURL(formUrl, 'cgid', this.form['cgid'].value);
        }

        $.ajax({
            context: this,
            url: formUrl,
            method: 'GET',
            success: (response) => processContentResponse(response, formUrl, searchTerm),
        });

    } else {
        setupSelector(searchNoResultsInfo, 'add', 'hidden');
        setupSelector(mostPopularSearches, 'remove', 'hidden');
        setupSelector(suggestionsResultContainer, 'add', 'hidden');
        setupSelector(suggestionsDefaultContainer, 'remove', 'hidden');
    }

    if (searchTerm.length > 0) {
        setupSelector(searchContainer, 'add', 'search-text-typed');
    } else {
        setupSelector(searchContainer, 'remove', 'search-text-typed');
    }
}

/**
 * Prevents submitting an empty search from the searchbar.
 * @return {[type]} [description]
 */
function preventEmptySearch() {
    addEventListenerList('[data-search-component="form"]', 'submit', function(evt) {
        let currentInput = this.querySelector('[data-search-component="query-input"]');

        if (!currentInput.value.trim()) {
            evt.preventDefault();

            return false;
        } else {
            _handleSearchTracking({
                searchTerm: currentInput.value,
                searchCategory: SEARCH_CATEGORY
            });
        }
    });
}

function setupSearchTrigger() {
    callbackOnElements('[data-search-component="trigger"]', function(currentTrigger) {
        if (currentTrigger && currentTrigger.toggle) {
            currentTrigger.toggle.afterSet = function() {
                let queryInputEl = currentTrigger.toggle.targetEl.querySelector('[data-search-component="query-input"]');

                disableBodyScroll(currentTrigger.toggle.targetEl, {allowTouchMove: allowScrollableAreas});

                setOverlay({trigger: 'searchbar'});

                getSuggestions.call(queryInputEl);
            };

            currentTrigger.toggle.afterUnset = function() {
                enableBodyScroll(currentTrigger.toggle.targetEl);

                unsetOverlay({trigger: 'searchbar'});
            };
        }
    });

}

/**
 * Toggle search bar off when megamenu is opened.
 */
export function closeSearch() {
    let searchTrigger = document.querySelector('[data-search-component="trigger"].toggle--active');

    if (searchTrigger && searchTrigger.toggle) {
        searchTrigger.toggle.unset();
    }
}

export function resetFaqSearch() {
    let faqSearchInput = document.querySelector('[data-component="faq-search-query-input"]'),
        searchContainer = document.querySelector('[data-cms-search]'),
        suggestionsDefaultContainer = document.querySelector('[data-component="faq-search-suggestions-default"]'),
        suggestionsResultContainer = document.querySelector('[data-component="faq-search-suggestions-result"]'),
        searchNoResultsInfo = document.querySelector('[data-component="faq-search-no-results-info"]'),
        mostPopularSearches = document.querySelector('[data-component="cms-help-popular-searches"]'),
        suggestionsContainer = getSuggestionsContainer(this);

    hideSrpNoResult();

    if (faqSearchInput) {
        faqSearchInput.value = '';
        suggestionsResultContainer.innerHTML = ''
        setupSelector(searchNoResultsInfo, 'add', 'hidden');
        setupSelector(mostPopularSearches, 'remove', 'hidden');
        setupSelector(suggestionsDefaultContainer, 'remove', 'hidden');
        setupSelector(searchContainer, 'remove', 'search-text-typed');
        setupSelector(suggestionsContainer, 'remove', 'set--invisible');
    }
    if (isSRP) {
        let srpPageContent = document.querySelector('[data-component="srp-page-content"]'),
            srpSearchTerm = document.querySelector('[data-search-heading]'),
            srpNewSearch = document.querySelector('[data-search-heading-srp]');
        setupSelector(srpPageContent, 'add', 'hidden');
        setupSelector(srpSearchTerm, 'add', 'hidden');
        setupSelector(srpNewSearch, 'remove', 'hidden');
    }
}

export function hideSrpNoResult() {
    if (isSRP) {
        let srpNoResult = document.querySelector('[data-noresult-is-srp-true]');

        if (srpNoResult) {
            setupSelector(srpNoResult, 'add', 'hidden');
        }
    }
}

export function getDefaultSuggestions(currentInput) {
    let formUrl = currentInput.getAttribute('data-getsuggestion-action'),
        defaultQuery = currentInput.getAttribute('data-default-search-query'),
        suggestionsDefaultContainer = document.querySelector('[data-component="faq-search-suggestions-default"]'),
        suggestionsDefaultAdded = suggestionsDefaultContainer.classList.contains('suggestions-default-added');

    if (!suggestionsDefaultAdded) {
        $.ajax({
            url: formUrl,
            type: 'GET',
            data: {
                q: defaultQuery,
                isFocusRequest: true
            },
            success: function(response) {
                processContentResponse(response, formUrl, defaultQuery, 'isDefault');
                setupSelector(suggestionsDefaultContainer, 'add', 'suggestions-default-added');
            },
            error: function() {
                console.log('Error!');
            }
        });
    } else {
        setupSelector(suggestionsDefaultContainer, 'remove', 'hidden');
    }
}

export function onFocusSearchInput() {
    let suggestionsDefaultContainer = document.querySelector('[data-component="faq-search-suggestions-default"]'),
        isSrpInput = this.getAttribute('data-is-srp'),
        thisLength = this.value.length;

    if (isSrpInput && thisLength > 0) {
        setupSelector(suggestionsDefaultContainer, 'add', 'hidden');
    } else {
        setupSelector(suggestionsDefaultContainer, 'remove', 'hidden');
    }

    this.removeEventListener('focus', onFocusSearchInput);
    showSearchResultOverlay();
}

export function onHelpSuggestionBack() {
    let suggestionsDefaultContainer = document.querySelector('[data-component="faq-search-suggestions-default"]'),
        faqSearchInput = document.querySelector('[data-component="faq-search-query-input"]');

    resetFaqSearch();

    setupSelector(suggestionsDefaultContainer, 'add', 'hidden');
    document.querySelector('body').classList.remove('set--has-search-results');
    faqSearchInput.addEventListener('focus', onFocusSearchInput);
}

export function onFaqSearchInputClose() {
    resetFaqSearch();
}

export function onFaqSearchFilterClick() {
    let currentFilter = this.getAttribute('data-filter-name'),
        totalFilterCount = parseInt(this.getAttribute('data-folder-count'), 10),
        currentFilterLimitToShow = parseInt(this.getAttribute('data-folder-limit-to-show'), 10),
        filterCountEle = document.querySelector('[data-faq-srp-articles-found-count]'),
        searchFiltersInfo = document.querySelectorAll('[data-component="faq-srp-articles-info"]'),
        currentFilterCountToShow = 0,
        filterCounter = 0;

    if (currentFilter == 'isAll') {

        [].forEach.call(searchFiltersInfo, function(searchFilterInfo) {
            let filterLength = searchFiltersInfo.length;

            filterCounter++;

            if ( filterCounter <= currentFilterLimitToShow) {
                setupSelector(searchFilterInfo, 'remove', 'hidden');
                currentFilterCountToShow = filterLength;
            } else {
                currentFilterCountToShow = currentFilterLimitToShow;
                setupSelector(searchFilterInfo, 'add', 'hidden');
            }

        });

        showSearchFilterActive(this, 'isAll');
    } else {
        [].forEach.call(searchFiltersInfo, function(searchFilterInfo) {
            let thisFilter = searchFilterInfo.getAttribute('data-filter-name');

            if (currentFilter == thisFilter) {
                filterCounter++;
                if ( filterCounter <= currentFilterLimitToShow) {
                    setupSelector(searchFilterInfo, 'remove', 'hidden');
                    currentFilterCountToShow = filterCounter;
                } else {
                    setupSelector(searchFilterInfo, 'add', 'hidden');
                }
            } else {
                setupSelector(searchFilterInfo, 'add', 'hidden');
            }

        });
        showSearchFilterActive(this);
    }

    filterCountEle.textContent = totalFilterCount;
    showItemsText(currentFilterCountToShow, totalFilterCount);
}

function showSearchFilterActive(_ele, isAll) {
    let searchFilterBtns = document.querySelectorAll('[data-component="faq-srp-articles-filter-btn"]'),
        searchFilterAllBtn = document.querySelector('[data-component="faq-srp-articles-filter-all"]');

    [].forEach.call(searchFilterBtns, function(searchFilterBtn) {
        setupSelector(searchFilterBtn, 'remove', 'link--secondary--static');
        setupSelector(searchFilterBtn, 'remove', 'set--active');
    });
    if (isAll == 'isAll') {
        setupSelector(searchFilterAllBtn, 'add', 'link--secondary--static');
        setupSelector(searchFilterAllBtn, 'add', 'set--active');
    } else {
        setupSelector(searchFilterAllBtn, 'remove', 'link--secondary--static');
        setupSelector(searchFilterAllBtn, 'remove', 'set--active');
        setupSelector(_ele, 'add', 'link--secondary--static');
        setupSelector(_ele, 'add', 'set--active');
    }
}

function showItemsText(showCount, totalCout) {
    let loadMoreBtn = document.querySelector('[data-component="faq-srp-load-more"]'),
        showingText = document.querySelector('[data-showing-text]'),
        filterText = showingText.getAttribute('data-showing-text'),
        afterFilterText = '';

    afterFilterText = filterText.replace('{0}', showCount);
    afterFilterText = afterFilterText.replace('{1}', totalCout);

    showingText.textContent = afterFilterText;

    if (showCount == totalCout) {
        setupSelector(loadMoreBtn, 'add', 'hidden');
    } else {
        setupSelector(loadMoreBtn, 'remove', 'hidden');
    }
}

function highlightSearchedTerm() {

    let searchQueryTerm = document.querySelector('[data-search-term="content-search-query"]').innerHTML.replace(/['"]+/g, ''),
        searchQueryResults = document.querySelectorAll('[data-help-content="search-content-result"]');

    searchQueryResults.forEach(searchQueryResult => {

        let matchingSearchKey = new RegExp('(' + searchQueryTerm + ')(?![^<]*>)', 'gi'),
            searchQueryPhrase = searchQueryResult.querySelector('.html-block__body').innerHTML,
            searchQueryMarked = searchQueryPhrase.replaceAll(matchingSearchKey, '<mark>' + searchQueryTerm + '</mark>');

        searchQueryResult.querySelector('.html-block__body').innerHTML = searchQueryMarked;
    });
}

export function onSrpLoadMore() {
    let self = this,
        activeElement = document.querySelector('[data-component="faq-srp-articles-filter-btn"].link--secondary--static'),
        totalFilterCount = 0;

    if (activeElement) {
        let filteName = activeElement.getAttribute('data-filter-name');

        totalFilterCount = parseInt(activeElement.getAttribute('data-folder-count'), 10);

        showMoreItem(filteName, totalFilterCount);
    } else {
        let activeElementAll = document.querySelector('[data-component="faq-srp-articles-filter-all"].link--secondary--static');

        if (activeElementAll) {
            totalFilterCount = parseInt(activeElementAll.getAttribute('data-folder-count'), 10);
        }
        showMoreItem('isAll', totalFilterCount);
    }

    setupSelector(self, 'add', 'hidden');
}

export function showMoreItem(filteName, totalFilterCount) {
    let searchFilterInfo = document.querySelectorAll('[data-component="faq-srp-articles-info"]');

    [].forEach.call(searchFilterInfo, function(searchFilter) {
        let curretnFilterName = searchFilter.getAttribute('data-filter-name');

        if (filteName == 'isAll') {
            setupSelector(searchFilter, 'remove', 'hidden');
        } else {
            if (filteName == curretnFilterName) {
                setupSelector(searchFilter, 'remove', 'hidden');
            } else {
                setupSelector(searchFilter, 'add', 'hidden');
            }
        }
    });

    showItemsText(totalFilterCount, totalFilterCount);
}

export function checkUrlFromSuggestionClick() {
    let contentId = '';

    if (isSRP) {
        contentId = document.querySelector('[data-component="srp-page-content"]').getAttribute('data-selected-content-id');
    } else {
        contentId = document.querySelector('[data-selected-content-id]').getAttribute('data-selected-content-id');
    }

    if (contentId != 'null') {
        let element = document.querySelector('#' + contentId),
            headerHeight = document.querySelector('[data-header-component="header"]').clientHeight;

        window.scrollTo({
            'behavior': 'smooth',
            'top': element.offsetTop - headerHeight
        });
    }
}

export function cmsSearchInit() {
    /**
     * Use debounce to avoid making an Ajax call on every single key press by waiting a few
     * hundred milliseconds before making the request. Without debounce, the user sees the
     * browser blink with every key press.
     */
    let debounceSuggestions = debounce(getSuggestions, 300),
        faqSearchInput = document.querySelector('[data-component="faq-search-query-input"]'),
        faqSearchInputClose = document.querySelector('[data-component="faq-search-input-close"]');

    faqSearchInput.addEventListener('focus', onFocusSearchInput);
    faqSearchInput.addEventListener('input', debounceSuggestions);

    faqSearchInputClose.addEventListener('click', onFaqSearchInputClose);

    if (isSRP) {
        let searchFilters = document.querySelectorAll('[data-component="faq-srp-articles-filter-btn"]'),
            searchFilterAll = document.querySelector('[data-component="faq-srp-articles-filter-all"]'),
            srpLoadMore = document.querySelector('[data-component="faq-srp-load-more"]'),
            srpSearchInput = document.querySelector('[data-component="faq-search-query-input"]');

        if (searchFilterAll) {
            searchFilterAll.addEventListener('click', onFaqSearchFilterClick);
            searchFilterAll.dispatchEvent(new Event('click'));
        }
        if (srpLoadMore) {
            srpLoadMore.addEventListener('click', onSrpLoadMore);
        }

        [].forEach.call(searchFilters, function(searchFilter) {
            searchFilter.addEventListener('click', onFaqSearchFilterClick);
        });

        srpSearchInput.addEventListener('input', function() {
            if (!this.value) {
                resetFaqSearch();
            }
        });

        highlightSearchedTerm();

    } else {
        let helpSuggestionBackBtn = document.querySelector('[data-help-suggestion-back]');

        helpSuggestionBackBtn.addEventListener('click', onHelpSuggestionBack);
    }

    preventEmptySearch();
    setupSearchTrigger();
    checkUrlFromSuggestionClick();
}
